import log from "loglevel";
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";

import { SUPPORT_CHAT_FUNNEL_DEFAULT, SUPPORT_CHAT_WS_URL } from "api-config.mjs";
import { extractAuthHeaders } from "api.mjs";
import { isBrowser } from "components/Browser";
import { SUPPORT_CHAT_FUNNEL } from "components/featureFlags";
import { useSessionContext } from "components/Session";
import { warmSupportChat } from "components/support-chat/warmSupportChat";
import useFeatureFlag from "hooks/useFeatureFlag";

import {
  FUNNEL_ENTRY_MESSAGE,
  OPENER_CHIPS,
  PANEL_OPENED_STORAGE_KEY,
  QUOTA_REPORTED_STORAGE_KEY,
  readSessionValue,
  writeSessionMark,
  writeSessionValue,
  PERSONA_NAMES,
  STARTER_SUGGESTIONS,
  chipFromWire,
  openerMessage,
  personaNamed,
  personaOnShift,
  WELCOME_MESSAGE,
  trackSupportChat,
} from "./funnel";

// App-level state for the support chat.
//
// Every page renders its own <Layout>, and Layout mounts the widget — so a
// Gatsby page change unmounts one widget and mounts a fresh one. The
// conversation must therefore live ABOVE the pages: this provider is mounted
// once from containers/App.js (Gatsby's wrapRootElement), which stays mounted
// across client-side navigation. It owns the thread, the conversation_id, the
// draft in the text box AND the WebSocket, so a reply that is still in flight
// when the visitor clicks a link lands in the chat on the next page.
//
// The thread is also mirrored to sessionStorage, so a full reload (or a link
// that isn't a Gatsby <Link>) restores it. sessionStorage, not localStorage,
// on purpose: it is per tab and dies with the tab, so a shared computer never
// shows one visitor's questions to the next, and a week-old conversation never
// reappears. Whether the panel is open is NOT persisted — a reload starts
// closed, the way the rest of the site does.
//
// This module is imported statically by App, so it must stay light: the
// markdown renderer and everything else UI lives in SupportChatWidget, which
// Layout loads lazily.
//
// Funnel mode (cca documentation/52-support-chat-funnel-bot.md) is decided here
// too, by the PostHog flag `supportChatFunnel` with the stage default as
// fallback: it swaps the opener, sends `funnel: true` on every query so the
// backend adds its plan tools, and keeps each answer's structured attachments
// (plan cards, buttons) on the message for the widget to render.

const RESPONSE_TIMEOUT_SECONDS = 120;
// Minimum gap between two pre-warms (see prewarm below).
const WARM_COOLDOWN_MS = 30_000;
const DEVICE_ID_STORAGE_KEY = "supportChatDeviceId";
const CONVERSATION_STORAGE_KEY = "supportChatConversation";
// Bump when the stored shape changes; an older version is discarded rather
// than half-read. 2: messages carry `attachments`, suggestions may be
// {label, message} objects (the opener chips) as well as strings. 3: the
// thread carries `personaName`, the team member who greeted it.
const CONVERSATION_STORAGE_VERSION = 3;

// Re-exported for the tests and the widget; the copy lives in ./funnel.
export { WELCOME_MESSAGE, STARTER_SUGGESTIONS };

// Shortest pause after a refusal, regardless of what the browser makes of the
// server's reset_on (see pauseUntil).
const MIN_QUOTA_PAUSE_SECONDS = 60;

// A pending bubble restored from storage can never resolve: the request_id it
// was waiting on died with the previous page load.
const RELOAD_INTERRUPTED_ERROR =
  "This reply was interrupted by a page reload — please ask again.";

// error_type values the backend sends (cca/support/worker.py) when the
// conversation_id we hold can no longer be continued: it was never stored, it
// has since been deleted, or it belongs to a different device id. The visible
// thread stays as it is; the next message simply opens a new conversation on
// the server instead of failing forever.
const STALE_CONVERSATION_ERROR_TYPES = new Set([
  "unknown_conversation",
  "conversation_forbidden",
]);

// A chip is either a plain string (sent verbatim, as the Haiku chips are) or
// {label, message, answer?}: the label is what the visitor sees, the message is
// what goes out, and `answer` ({step, value}), when present, rides the payload
// as funnel_answer so the backend's script answers the tap (cca/support/
// scripted.py) instead of the model.
export const chipLabel = (chip) => (typeof chip === "string" ? chip : chip.label);
export const chipMessage = (chip) => (typeof chip === "string" ? chip : chip.message);
export const chipAnswer = (chip) =>
  typeof chip === "string" || !chip.answer ? null : chip.answer;

const isFunnelAnswer = (answer) =>
  answer &&
  typeof answer === "object" &&
  typeof answer.step === "string" &&
  typeof answer.value === "string";

const isStoredChip = (chip) =>
  typeof chip === "string" ||
  (chip &&
    typeof chip === "object" &&
    typeof chip.label === "string" &&
    typeof chip.message === "string" &&
    (chip.answer === undefined || isFunnelAnswer(chip.answer)));

const isStoredAttachment = (attachment) =>
  attachment && typeof attachment === "object" && typeof attachment.type === "string";

const isStoredMessage = (message) =>
  message &&
  typeof message === "object" &&
  (message.role === "user" || message.role === "assistant") &&
  typeof message.text === "string" &&
  Array.isArray(message.badges) &&
  (message.attachments === undefined ||
    (Array.isArray(message.attachments) && message.attachments.every(isStoredAttachment)));

// A new thread is greeted by whoever is on duty now (personaOnShift); the
// name is stored with the thread so that it stays the same for as long as the
// thread does. Computed in both modes so the stored shape is one shape.
const freshConversation = (funnel) => {
  const persona = personaOnShift();
  return {
    messages: [funnel ? openerMessage(persona) : WELCOME_MESSAGE],
    conversationId: null,
    suggestions: funnel ? OPENER_CHIPS : STARTER_SUGGESTIONS,
    personaName: persona.name,
    // The mode this thread started in. Sent as `funnel` on every query of the
    // thread, whatever the flag resolves to later: a funnel chip answered by a
    // non-funnel backend turn (or the reverse) would break the conversation.
    funnel,
    shownPlanCards: [],
  };
};

// A conversation the visitor has not touched: only the scripted opener, no
// server-side thread. Safe to swap when the funnel flag resolves late.
const isUntouched = (messages, conversationId) =>
  conversationId === null && messages.length <= 1;

const loadStoredConversation = (funnel) => {
  if (!isBrowser()) {
    return freshConversation(funnel);
  }
  try {
    const stored = JSON.parse(sessionStorage.getItem(CONVERSATION_STORAGE_KEY));
    if (
      !stored ||
      stored.version !== CONVERSATION_STORAGE_VERSION ||
      !Array.isArray(stored.messages) ||
      stored.messages.length === 0 ||
      !stored.messages.every(isStoredMessage) ||
      !PERSONA_NAMES.includes(stored.personaName)
    ) {
      return freshConversation(funnel);
    }
    return {
      messages: stored.messages.map((message) => ({
        ...message,
        attachments: message.attachments || [],
        ...(message.pending ? { pending: false, error: RELOAD_INTERRUPTED_ERROR } : {}),
      })),
      conversationId:
        typeof stored.conversationId === "string" ? stored.conversationId : null,
      suggestions: Array.isArray(stored.suggestions)
        ? stored.suggestions.filter(isStoredChip)
        : [],
      personaName: stored.personaName,
      funnel: typeof stored.funnel === "boolean" ? stored.funnel : funnel,
      shownPlanCards: Array.isArray(stored.shownPlanCards)
        ? stored.shownPlanCards.filter((key) => typeof key === "string")
        : [],
    };
  } catch (error) {
    // Corrupt stored value — start over.
    return freshConversation(funnel);
  }
};

// Stable anonymous device id: the backend's per-device rolling quota keys on it.
const getDeviceId = () => {
  if (!isBrowser()) {
    return null;
  }
  let deviceId = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
  if (!deviceId) {
    deviceId = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_STORAGE_KEY, deviceId);
  }
  return deviceId;
};

const currentPage = () => (isBrowser() ? window.location.pathname : null);

const SupportChatContext = createContext(null);

export const useSupportChat = () => {
  const value = useContext(SupportChatContext);
  if (value === null) {
    throw new Error(
      "useSupportChat must be used inside <SupportChatProvider> (mounted in containers/App.js)"
    );
  }
  return value;
};

export const SupportChatProvider = ({ children }) => {
  // PostHog decides, the stage default answers first (and if PostHog never
  // does). Fails toward the stage default rather than open or closed: alpha-like
  // stages run the funnel, prod does not until the flag is ramped there.
  const { enabled: funnelEnabled } = useFeatureFlag(SUPPORT_CHAT_FUNNEL, {
    fallback: SUPPORT_CHAT_FUNNEL_DEFAULT,
  });
  const [initial] = useState(() => loadStoredConversation(funnelEnabled));
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(initial.messages);
  const [inputValue, setInputValue] = useState("");
  const [isWaiting, setIsWaiting] = useState(false);
  const [conversationId, setConversationId] = useState(initial.conversationId);
  // Epoch ms until which the server has refused (or will refuse) this device's
  // messages, or null. Set from the last answer's quota at zero remaining and
  // from a quota_exceeded refusal; cleared by a timer at that instant so the
  // box reopens on its own instead of staying dead until a page reload.
  const [pausedUntil, setPausedUntil] = useState(null);
  const [suggestions, setSuggestions] = useState(initial.suggestions);
  // The team member who greeted this thread (see personaOnShift in ./funnel).
  const [personaName, setPersonaName] = useState(initial.personaName);
  const [threadFunnel, setThreadFunnel] = useState(initial.funnel);
  // Plan cards already counted as recommendations, keyed by message and plan,
  // so a re-render, a panel reopened or a restored thread does not count one
  // twice. Stored with the thread (not derived from it: a card that landed
  // behind a closed panel and was restored after a reload has not been seen
  // yet) and cleared with it (clearConversation), which is what scopes the
  // key: the conversation id is deliberately not part of it, because a thread
  // the server no longer knows keeps its messages and continues under a new id
  // (restartedConversationRef), and the next remount would have counted every
  // old card again under that id (Codex, PR #838). The ref answers
  // synchronously within a render; the state mirrors it into storage.
  const shownPlanCardsRef = useRef(new Set(initial.shownPlanCards));
  const [shownPlanCards, setShownPlanCards] = useState(initial.shownPlanCards);
  const reportPlanCardShown = (messageIndex, card) => {
    const key = `${messageIndex}|${card.plan}`;
    if (shownPlanCardsRef.current.has(key)) {
      return;
    }
    shownPlanCardsRef.current.add(key);
    setShownPlanCards(Array.from(shownPlanCardsRef.current));
    trackSupportChat("SupportChatPlanRecommended", {
      planName: card.plan,
      upsell: card.upsell || null,
      // Set when this card is the tier above, or the other family's
      // alternative, shown beside the recommendation named here.
      upsellOf: card.upsellOf || null,
      alternativeOf: card.alternativeOf || null,
      conversation_id: conversationId,
    });
  };
  const { sessionContext } = useSessionContext();
  const timeoutRef = useRef(null);
  const abandonedRequestIdRef = useRef(null);
  const didUnmount = useRef(false);
  // request_id of the in-flight query. Server chunks echo the request_id they
  // answer; anything else (e.g. a reply arriving after the 120s timeout, once
  // the user has already sent a new message) is stale and must be ignored, or
  // it would resolve the wrong bubble and overwrite conversation_id/quota.
  const pendingRequestIdRef = useRef(null);
  // The in-flight query ({text, funnelAnswer}), kept so it can be re-sent on a
  // fresh conversation if the server no longer knows the stored conversation_id.
  const pendingQueryRef = useRef(null);
  // Set once the in-flight query has been re-sent on a fresh conversation, so
  // a second stale-conversation error cannot loop.
  const restartedConversationRef = useRef(false);
  // request_id of the most recently COMPLETED query. The suggested_replies
  // chunk arrives after query_response has already cleared pendingRequestIdRef,
  // so it needs its own staleness gate: chips must belong to the answer the
  // visitor is looking at, never to an earlier turn.
  const lastCompletedRequestIdRef = useRef(null);
  // Set by openPanel and consumed (once) by the widget, which moves focus into
  // the dialog. A ref rather than an effect on isOpen in the widget: the
  // widget remounts on every page change while the panel stays open, and an
  // isOpen effect would grab focus — and pop the phone keyboard — on each one.
  const focusRequestRef = useRef(false);
  // A message to send as soon as the socket is open (openWithFunnel): the
  // socket only connects once the panel opens, so the send cannot happen in the
  // same tick as the open.
  const autoSendRef = useRef(null);

  useEffect(() => {
    return () => {
      didUnmount.current = true;
      clearResponseTimeout();
    };
  }, []);

  // The flag can resolve after the first render (PostHog answers in the
  // browser). While the visitor has not touched the thread, follow it, so an
  // alpha visitor never sits on the old welcome text; once there is a real
  // conversation the opener stays whatever it was. The same rule moves an
  // untouched opener restored from storage on to the team member now on duty:
  // nobody has spoken yet, so the greeting is whoever is on shift.
  useEffect(() => {
    setMessages((prevMessages) => {
      if (!isUntouched(prevMessages, conversationId)) {
        return prevMessages;
      }
      const fresh = freshConversation(funnelEnabled);
      if (prevMessages[0]?.text === fresh.messages[0].text) {
        return prevMessages;
      }
      setSuggestions(fresh.suggestions);
      setPersonaName(fresh.personaName);
      setThreadFunnel(fresh.funnel);
      return fresh.messages;
    });
    // conversationId is read, not reacted to: a conversation that starts after
    // the flag resolved must not be reset by this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [funnelEnabled]);

  // Pre-warm the chat Lambdas on intent, not on page load. Each warm starts a
  // support-chat worker container on the backend, and only about 1 in 250
  // sessions ever sends a chat turn (last 30 days to 2026-09-10: ~114 support
  // turns against ~32k sessions), so a warm on every page load paid for ~249
  // containers nobody used -- plus one per crawl by Googlebot and the other
  // JavaScript-rendering bots, invisible in PostHog. Hover or keyboard focus
  // on the bubble is the earliest signal a visitor is about to open the chat,
  // and the panel opening is the next; the visitor still has to type, which
  // is longer than the ~4 s the worker needs to boot (CloudWatch trace in
  // PR #736). Touch devices have no hover and go straight to the open call.
  //
  // The cooldown collapses a hover followed by a click, or a mouse jittering
  // over the bubble, into a single warm. It is short on purpose: a visitor
  // who hovers, wanders off and comes back later gets a fresh warm, because
  // Lambda reclaims an idle container after some minutes. See
  // warmSupportChat.js.
  const lastWarmAtRef = useRef(0);
  const prewarm = () => {
    const now = Date.now();
    if (now - lastWarmAtRef.current < WARM_COOLDOWN_MS) {
      return;
    }
    lastWarmAtRef.current = now;
    warmSupportChat(SUPPORT_CHAT_WS_URL);
  };

  // Mirror the thread to sessionStorage (see the header comment). pausedUntil
  // is deliberately left out: a stored pause would keep the box disabled past
  // the reset, and the server re-reports the quota with every answer anyway.
  useEffect(() => {
    if (!isBrowser()) {
      return;
    }
    try {
      sessionStorage.setItem(
        CONVERSATION_STORAGE_KEY,
        JSON.stringify({
          version: CONVERSATION_STORAGE_VERSION,
          messages,
          conversationId,
          suggestions,
          personaName,
          funnel: threadFunnel,
          shownPlanCards,
        })
      );
    } catch (error) {
      log.warn("support-chat: could not persist the conversation", error);
    }
  }, [messages, conversationId, suggestions, personaName, threadFunnel, shownPlanCards]);

  // Connect while the panel is open (third arg) and reconnect with backoff
  // like the investor chat (ChatContainer.js). Also stay connected while a
  // reply is pending, so closing the panel mid-answer doesn't lose it.
  const { sendMessage, lastMessage, readyState } = useWebSocket(
    SUPPORT_CHAT_WS_URL,
    {
      shouldReconnect: () => didUnmount.current === false,
      reconnectAttempts: 10,
      reconnectInterval: (attemptNumber) =>
        Math.min(Math.pow(2, attemptNumber) * 1_000, 10_000),
    },
    isOpen || isWaiting
  );

  const clearResponseTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const failPendingMessage = (errorText) => {
    pendingRequestIdRef.current = null;
    pendingQueryRef.current = null;
    setMessages((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last?.pending) {
        next[next.length - 1] = { ...last, pending: false, error: errorText };
      }
      return next;
    });
    setIsWaiting(false);
  };

  // reset_on is the server's clock. A value the browser reads as already
  // passed (clock skew, or a server that sent none) still means "refused right
  // now", so pause for at least a minute rather than inviting an immediate
  // resend that the server would refuse again — and the refusal would re-arm
  // the pause with a fresh reset_on anyway.
  const pauseUntil = (resetOn) => {
    const at = Date.parse(resetOn);
    const floor = Date.now() + MIN_QUOTA_PAUSE_SECONDS * 1_000;
    setPausedUntil(Number.isFinite(at) ? Math.max(at, floor) : floor);
  };
  // One SupportChatQuotaExhausted per spent window, whichever way it was
  // noticed: the last allowed reply landing with nothing remaining (the usual
  // way, since the input then pauses and no refused send follows), or a
  // refusal from the worker. Keyed by the window's reset time.
  // Keyed by the window's reset time, and kept with the tab: a reload before
  // the reset forgets the pause on purpose, and the refusal that follows must
  // not count the same spent window again.
  const reportQuotaExhausted = (resetOn, via, conversation) => {
    // The server always says when the window resets (cca worker,
    // remaining_usage); without it there is no window to deduplicate by, so
    // the exhaustion is reported each time rather than once per tab for good.
    if (resetOn && readSessionValue(QUOTA_REPORTED_STORAGE_KEY) === resetOn) {
      return;
    }
    if (resetOn) {
      writeSessionValue(QUOTA_REPORTED_STORAGE_KEY, resetOn);
    }
    trackSupportChat("SupportChatQuotaExhausted", { conversation_id: conversation, via });
  };

  const startResponseTimeout = () => {
    clearResponseTimeout();
    timeoutRef.current = setTimeout(() => {
      log.error("support-chat: server did not respond in time");
      trackSupportChat("SupportChatError", { error_type: "timeout", conversation_id: conversationId });
      failPendingMessage("The server did not respond in time. Please try again.");
    }, RESPONSE_TIMEOUT_SECONDS * 1_000);
  };

  // Put a query on the wire. Everything visible (bubbles, chips, the draft)
  // is sendQuery's job — this is also what re-sends a query on a fresh
  // conversation, where nothing visible should change.
  const transmit = (query, threadId, funnelAnswer = null) => {
    const headers = extractAuthHeaders({ context: { sessionContext } });
    const requestId = crypto.randomUUID();
    pendingRequestIdRef.current = requestId;
    pendingQueryRef.current = { query, funnelAnswer };
    const payload = {
      query,
      conversation_id: threadId,
      action_handler: "support",
      headers,
      device_id: getDeviceId(),
      request_id: requestId,
      // Frontend-owned rollout: the backend adds its plan tools and prompt
      // block only when this is literally true (cca/support/worker.py).
      funnel: threadFunnel,
      // A tapped funnel chip (or the email card): the backend answers it from
      // its script, with no model call. Absent for typed text.
      ...(funnelAnswer ? { funnel_answer: funnelAnswer } : {}),
    };
    // headers carries the visitor's real session token, and alpha logs at
    // debug on a publicly reachable deployment — never print it (#684 review).
    log.debug("support-chat: sending", { ...payload, headers: "[redacted]" });
    sendMessage(JSON.stringify(payload));
    startResponseTimeout();
  };

  const handleServerMessage = (message) => {
    let parsed;
    try {
      parsed = JSON.parse(message.data);
    } catch (error) {
      log.error("support-chat: unparseable message", message.data);
      return;
    }
    log.debug("support-chat: received", parsed);
    const { status, message_type: messageType } = parsed;
    if (abandonedRequestIdRef.current !== null) {
      // The reply a Clear abandoned: its chunks are dropped, and it is retired
      // only by a terminal one (its answer, or an error), so that its progress
      // chunks do not clear the marker before a last untagged error.
      const untaggedError = status === "error" && parsed.request_id === undefined;
      if (parsed.request_id === abandonedRequestIdRef.current) {
        log.debug("support-chat: dropping a chunk of the abandoned request");
        if (status === "error" || messageType === "query_response") {
          abandonedRequestIdRef.current = null;
        }
        return;
      }
      if (untaggedError) {
        // An error with no request id cannot be told apart between the
        // abandoned reply and a fresh question on the same socket. With no
        // question pending it can only be the abandoned reply's: dropped. With
        // one pending it is applied to the fresh question below: a failure the
        // visitor can see and resend beats one hidden until the 120 s timeout,
        // and the worker tags its own errors with the request id, so an
        // untagged one is the rare dispatcher-level kind. Either way nothing
        // more is expected of the abandoned reply.
        abandonedRequestIdRef.current = null;
        if (pendingRequestIdRef.current === null) {
          log.debug("support-chat: dropping the abandoned request's untagged error");
          return;
        }
      }
    }
    if (status === "error") {
      // Mirror of the stale-chunk gate below (#684 review): error chunks that
      // can straggle in after the 120s timeout — invalid query, unknown
      // conversation, quota_exceeded (cca/support/worker.py) — echo the
      // request_id they answer, and a stale one must not fail the NEW
      // message's live bubble. Errors WITHOUT a request_id (invalid JSON,
      // auth, internal error) always answer the live request; apply as before.
      if (
        parsed.request_id !== undefined &&
        parsed.request_id !== pendingRequestIdRef.current
      ) {
        log.debug(
          "support-chat: dropping stale error chunk for request",
          parsed.request_id
        );
        return;
      }
      if (
        STALE_CONVERSATION_ERROR_TYPES.has(parsed.error_type) &&
        conversationId !== null &&
        pendingQueryRef.current !== null &&
        !restartedConversationRef.current
      ) {
        // The stored conversation is gone on the server (a restored thread
        // whose conversation was deleted, or a device id that no longer
        // matches). Keep the visible thread, drop the id, and re-send the
        // same question as the first message of a new conversation. The
        // server refuses before consuming quota, so the retry costs one turn.
        log.warn(
          "support-chat: server no longer knows conversation",
          conversationId,
          "— starting a new one"
        );
        restartedConversationRef.current = true;
        setConversationId(null);
        transmit(pendingQueryRef.current.query, null, pendingQueryRef.current.funnelAnswer);
        return;
      }
      if (parsed.error_type === "quota_exceeded") {
        // The window is spent; reset_on says when it reopens. The message
        // already tells the visitor the same thing, in the failed bubble.
        pauseUntil(parsed.reset_on);
      }
      clearResponseTimeout();
      if (parsed.error_type === "quota_exceeded") {
        reportQuotaExhausted(parsed.reset_on, "refused", conversationId);
      } else {
        trackSupportChat("SupportChatError", {
          error_type: parsed.error_type || "server_error",
          conversation_id: conversationId,
        });
      }
      failPendingMessage(
        parsed.message || "Something went wrong. Please try again."
      );
      return;
    }
    if (status !== "success") {
      return;
    }
    if (messageType === "suggested_replies") {
      // Arrives after query_response, when pendingRequestIdRef is already null,
      // so the in-flight gate below would wrongly drop it. Its own gate: the
      // chunk must belong to the query that just completed, and no new query
      // may be in flight (late chips must not attach to the wrong answer).
      if (
        pendingRequestIdRef.current !== null ||
        parsed.request_id !== lastCompletedRequestIdRef.current
      ) {
        log.debug(
          "support-chat: dropping stale suggested_replies chunk",
          parsed.request_id
        );
        return;
      }
      setSuggestions(parsed.result.suggested_replies.map(chipFromWire));
      return;
    }
    // Success chunks carry the request_id of the query they answer (error
    // chunks don't — the worker's error paths omit it). Drop anything that
    // isn't answering the current in-flight request.
    if (parsed.request_id !== pendingRequestIdRef.current) {
      log.debug(
        "support-chat: dropping stale chunk for request",
        parsed.request_id
      );
      return;
    }
    if (messageType === "tool_calls") {
      startResponseTimeout();
      // Surface progress as a status badge on the pending assistant message,
      // e.g. "Checking coverage...", "Searching the docs...".
      const badge = parsed.agent_message;
      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last?.pending && badge && !last.badges.includes(badge)) {
          next[next.length - 1] = { ...last, badges: [...last.badges, badge] };
        }
        return next;
      });
    } else if (messageType === "tool_call_response") {
      startResponseTimeout();
    } else if (messageType === "query_response") {
      clearResponseTimeout();
      lastCompletedRequestIdRef.current = pendingRequestIdRef.current;
      pendingRequestIdRef.current = null;
      pendingQueryRef.current = null;
      const { result } = parsed;
      // Older workers send no attachments; a list is what the widget renders.
      const attachments = Array.isArray(result.query.attachments)
        ? result.query.attachments
        : [];
      setConversationId(result.conversation_id);
      if (result.quota.remaining_daily_queries <= 0) {
        // The answer landed but the window is now spent: the next send would
        // be refused, so pause now and reopen at the window's reset.
        pauseUntil(result.quota.reset_on);
        reportQuotaExhausted(result.quota.reset_on, "last_reply", result.conversation_id);
      }
      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        next[next.length - 1] = {
          ...last,
          pending: false,
          text: result.query.answer,
          attachments,
        };
        return next;
      });
      // SupportChatPlanRecommended is recorded by the widget when the card is on
      // screen (reportPlanCardShown), not here: the panel can be closed while
      // the answer lands, and a card nobody saw is not a recommendation.
      if (result.query.lead_captured === true) {
        // The worker says this turn made the conversation a CRM lead (an email in
        // the funnel, with or without a plan yet). Reported from the browser, not
        // the worker, so GA4 attributes the conversion to the session's source.
        // GA4's recommended lead event and parameter, so it can be marked a key
        // event as-is and imported by Google Ads:
        // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#generate_lead
        trackSupportChat("GenerateLead", {
          lead_source: "support_chat",
          conversation_id: result.conversation_id,
        });
      }
      setIsWaiting(false);
    }
  };

  useEffect(() => {
    if (lastMessage !== null) {
      handleServerMessage(lastMessage);
    }
  }, [lastMessage]);

  const quotaExhausted = pausedUntil !== null;

  useEffect(() => {
    if (pausedUntil === null) {
      return undefined;
    }
    const timer = setTimeout(
      () => setPausedUntil(null),
      Math.max(0, pausedUntil - Date.now())
    );
    return () => clearTimeout(timer);
  }, [pausedUntil]);

  // Shared by the input form and the suggestion chips — a chip click is exactly
  // a pre-typed send, so every guard applies to both paths. Returns whether a
  // message actually went out. `via` says how the message came to be sent
  // (typed / chip / opener / teaser / email_card) and is what the analytics
  // carry; `funnelAnswer` ({step, value}) marks a scripted answer.
  const sendQuery = (text, { via = "typed", funnelAnswer = null } = {}) => {
    const query = text.trim();
    if (!query || isWaiting || quotaExhausted) {
      return false;
    }
    if (readyState !== ReadyState.OPEN) {
      log.error("support-chat: websocket not open, state:", readyState);
      return false;
    }
    restartedConversationRef.current = false;
    transmit(query, conversationId, funnelAnswer);
    trackSupportChat("SupportChatMessageSent", {
      page: currentPage(),
      via,
      funnel: threadFunnel,
      conversation_id: conversationId,
      // Free text: withheld from GA, kept for PostHog and the ES index.
      excludeFromGtag: { query },
    });
    setMessages((prev) => [
      ...prev,
      { role: "user", text: query, badges: [], attachments: [] },
      { role: "assistant", text: "", badges: [], attachments: [], pending: true },
    ]);
    // Chips answer the previous turn; once a new query is out they're stale.
    // Fresh ones arrive with the next answer's suggested_replies chunk.
    setSuggestions([]);
    setInputValue("");
    setIsWaiting(true);
    return true;
  };

  // A tapped chip: the label is what the visitor saw, the message is what goes
  // out. Recorded as a choice so the qualification answers are countable: a
  // funnel chip's step and fixed value ("use" / "personal") go to every sink.
  // The label itself is withheld from GA: a Haiku-generated chip is free text
  // and can quote the visitor's email verbatim ("It's bob@example.com"), which
  // GA's policy forbids; PostHog and the ES index still get it.
  const sendChip = (chip) => {
    const label = chipLabel(chip);
    const answer = chipAnswer(chip);
    const sent = sendQuery(chipMessage(chip), { via: "chip", funnelAnswer: answer });
    if (sent) {
      trackSupportChat("SupportChatChoiceSelected", {
        question: answer ? answer.step : null,
        value: answer ? answer.value : null,
        conversation_id: conversationId,
        excludeFromGtag: { label },
      });
    }
    return sent;
  };

  // Deferred send for openWithFunnel: fires once the socket the open triggered
  // is actually up. Cleared on fire so a reconnect never re-sends it.
  useEffect(() => {
    if (isOpen && readyState === ReadyState.OPEN && autoSendRef.current !== null) {
      const pending = autoSendRef.current;
      autoSendRef.current = null;
      sendQuery(pending.text, { via: pending.via });
    }
    // sendQuery is recreated every render; the refs and readyState carry the
    // state that matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, readyState]);

  // `focus: false` for an open the visitor did not ask for (the auto-open timer,
  // SupportChatWidget.js): pulling the cursor out of whatever they were doing
  // on the page would be the interruption the panel itself is careful not to be.
  // What opened the panel ("bubble", "auto", "teaser"): an auto-opened panel is
  // closed again on a page the auto-open stays off (SupportChatWidget.js).
  const [openTrigger, setOpenTrigger] = useState(null);
  const openPanel = (trigger = "bubble", { focus = true } = {}) => {
    focusRequestRef.current = focus;
    setOpenTrigger(trigger);
    setIsOpen(true);
    if (isBrowser()) {
      // Any open, however triggered, is what stops the auto-open from ever
      // firing in this tab (see the note on the key in ./funnel).
      writeSessionMark(PANEL_OPENED_STORAGE_KEY);
    }
    trackSupportChat("SupportChatOpened", { page: currentPage(), trigger });
    // Covers touch devices (no hover) and a tab that sat idle after the hover
    // warm; the cooldown makes a hover-then-click a single warm. Not for a
    // panel that opened by itself: that is no intent, and warming a worker for
    // every tab that stays open four seconds is the waste intent-only warming
    // exists to avoid (the first hover, focus or send warms it).
    if (trigger !== "auto") {
      prewarm();
    }
  };
  // Open with "Find the right plan" already selected (the teaser's tap).
  const openWithFunnel = (trigger = "teaser") => {
    if (!isWaiting && !quotaExhausted) {
      autoSendRef.current = { text: FUNNEL_ENTRY_MESSAGE, via: trigger };
    }
    openPanel(trigger);
  };
  const closePanel = () => {
    // A teaser tap queued "I'd like help choosing a plan" for when the socket
    // opens; closing before it fired must not leave it to fire on a later,
    // ordinary open.
    autoSendRef.current = null;
    setOpenTrigger(null);
    setIsOpen(false);
  };
  const togglePanel = () => (isOpen ? closePanel() : openPanel("bubble"));

  // "Clear chat": back to the scripted opener, as if the visitor had just
  // opened the widget for the first time. The server-side conversation is left
  // alone (it is the admin's transcript); the next message simply starts a new
  // one. A reply still in flight is abandoned: its request_id is forgotten, so
  // the chunks that answer it fail the staleness gates above and never land in
  // the fresh thread. pausedUntil stays -- it is the device's allowance, not
  // thread state.
  const canClear = !isUntouched(messages, conversationId) || isWaiting;
  const clearConversation = () => {
    if (!canClear) {
      return;
    }
    clearResponseTimeout();
    // A reply still in flight is abandoned, not awaited: the box reopens at
    // once. Its chunks are dropped by id when they land; an error WITHOUT an
    // id (auth, internal) that lands while it is outstanding is taken as its
    // and dropped too, so it cannot fail the fresh thread's first question.
    abandonedRequestIdRef.current = pendingRequestIdRef.current;
    pendingRequestIdRef.current = null;
    pendingQueryRef.current = null;
    lastCompletedRequestIdRef.current = null;
    restartedConversationRef.current = false;
    autoSendRef.current = null;
    trackSupportChat("SupportChatCleared", {
      page: currentPage(),
      conversation_id: conversationId,
      turns: messages.filter((message) => message.role === "user").length,
    });
    const fresh = freshConversation(funnelEnabled);
    setMessages(fresh.messages);
    setSuggestions(fresh.suggestions);
    setPersonaName(fresh.personaName);
    setThreadFunnel(fresh.funnel);
    shownPlanCardsRef.current = new Set();
    setShownPlanCards([]);
    setConversationId(null);
    setInputValue("");
    setIsWaiting(false);
  };
  const takeFocusRequest = () => {
    const requested = focusRequestRef.current;
    focusRequestRef.current = false;
    return requested;
  };

  return (
    <SupportChatContext.Provider
      value={{
        isOpen,
        openPanel,
        openWithFunnel,
        openTrigger,
        closePanel,
        togglePanel,
        prewarm,
        takeFocusRequest,
        // The live flag: gates things that START a flow (auto-open, the teaser).
        funnelEnabled,
        // The mode this thread is in, frozen when it started or was restored and
        // sent on every request: what the conversation UI (header, opener) must
        // follow, so a flag change mid-conversation does not restyle a thread
        // the backend is still answering in its original mode.
        threadFunnel,
        persona: personaNamed(personaName),
        conversationId,
        messages,
        suggestions,
        isWaiting,
        quotaExhausted,
        quotaResumesAt: pausedUntil,
        readyState,
        inputValue,
        setInputValue,
        sendQuery,
        sendChip,
        reportPlanCardShown,
        canClear,
        clearConversation,
        // A conversation exists (something was sent): the auto-open leaves a
        // restored thread alone.
        threadTouched: !isUntouched(messages, conversationId),
      }}
    >
      {children}
    </SupportChatContext.Provider>
  );
};

export default SupportChatProvider;
